import React, { useState, useEffect } from "react";
import "./PagesCMS.css";
import ShadcnActionDropdown from "../common/ShadcnActionDropdown/ShadcnActionDropdown";
import AddContentModal from "./AddContentModal/AddContentModal";
import EditContentModal from "./EditContentModal/EditContentModal";
import {
  getAllPages,
  getSectionsByPage,
  getContentByPage,
  getContentByPageAndSection,
  getCmsStats,
  deleteContent,
  toggleContentStatus,
} from "../../services/cmsService";
import LoadingSpinner from "../common/LoadingSpinner/LoadingSpinner";

const PagesCMS = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [content, setContent] = useState([]);
  const [stats, setStats] = useState({});

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);

  // Fetch initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch data when page or section changes
  useEffect(() => {
    if (selectedPage) {
      fetchSections();
      fetchContent();
    }
  }, [selectedPage, selectedSection]);

  const fetchInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [pagesResponse, statsResponse] = await Promise.allSettled([
        getAllPages(),
        getCmsStats()
      ]);

      if (pagesResponse.status === 'fulfilled') {
        setPages(pagesResponse.value.data || []);
        if (pagesResponse.value.data && pagesResponse.value.data.length > 0) {
          setSelectedPage(pagesResponse.value.data[0]);
        }
      }

      if (statsResponse.status === 'fulfilled') {
        setStats(statsResponse.value.data || {});
      }

      if (pagesResponse.status === 'rejected' && statsResponse.status === 'rejected') {
        setError("Failed to fetch CMS data. Please check your connection.");
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      setError("Failed to fetch CMS data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSections = async () => {
    if (!selectedPage) return;

    try {
      const response = await getSectionsByPage(selectedPage);
      setSections(response.data || []);
      if (response.data && response.data.length > 0) {
        setSelectedSection(response.data[0]);
      } else {
        setSelectedSection("");
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      setSections([]);
    }
  };

  const fetchContent = async () => {
    if (!selectedPage) return;

    try {
      let response;
      if (selectedSection) {
        response = await getContentByPageAndSection(selectedPage, selectedSection);
      } else {
        response = await getContentByPage(selectedPage);
      }
      setContent(response.data || []);
    } catch (error) {
      console.error("Error fetching content:", error);
      setContent([]);
    }
  };

  const handleDeleteContent = async (contentId) => {
    if (!window.confirm("Are you sure you want to delete this content?")) {
      return;
    }

    try {
      await deleteContent(contentId);
      fetchContent(); // Refresh content
    } catch (error) {
      console.error("Error deleting content:", error);
      alert("Failed to delete content");
    }
  };

  const handleToggleStatus = async (contentId) => {
    try {
      await toggleContentStatus(contentId);
      fetchContent(); // Refresh content
    } catch (error) {
      console.error("Error toggling content status:", error);
      alert("Failed to toggle content status");
    }
  };

  const handleEditContent = (content) => {
    setEditingContent(content);
    setIsEditModalOpen(true);
  };

  const handleContentSaved = () => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setEditingContent(null);
    fetchContent(); // Refresh content
  };

  const renderOverviewTab = () => (
    <div className="overview-tab">
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Content</h3>
          <p className="stat-number">{stats.totalContent || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Active Content</h3>
          <p className="stat-number">{stats.activeContent || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Inactive Content</h3>
          <p className="stat-number">{stats.inactiveContent || 0}</p>
        </div>
        <div className="stat-card">
          <h3>Total Pages</h3>
          <p className="stat-number">{pages.length}</p>
        </div>
      </div>

      <div className="content-by-page">
        <h3>Content by Page</h3>
        <div className="page-stats">
          {stats.contentByPage && Object.entries(stats.contentByPage).map(([page, count]) => (
            <div key={page} className="page-stat-item">
              <span className="page-name">{page}</span>
              <span className="page-count">{count} items</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderContentTab = () => (
    <div className="content-tab">
      <div className="content-controls">
        <div className="page-selector">
          <label>Page:</label>
          <select
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
          >
            {pages.map(page => (
              <option key={page} value={page}>{page}</option>
            ))}
          </select>
        </div>

        <div className="section-selector">
          <label>Section:</label>
          <select
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
          >
            <option value="">All Sections</option>
            {sections.map(section => (
              <option key={section} value={section}>{section}</option>
            ))}
          </select>
        </div>

        <button
          className="add-content-btn"
          onClick={() => setIsAddModalOpen(true)}
        >
          Add Content
        </button>
      </div>

      <div className="content-list">
        {content.length === 0 ? (
          <div className="no-content">
            <p>No content found for this page/section.</p>
            <button onClick={() => setIsAddModalOpen(true)}>Add First Content</button>
          </div>
        ) : (
          <div className="content-grid">
            {content.map((item) => (
              <div key={item.id} className={`content-card ${!item.isActive ? 'inactive' : ''}`}>
                <div className="content-header">
                  <h4>{item.title || 'Untitled'}</h4>
                  <ShadcnActionDropdown
                    items={[
                      {
                        label: item.isActive ? 'Deactivate' : 'Activate',
                        onClick: () => handleToggleStatus(item.id),
                        icon: item.isActive ? 'eye-off' : 'eye'
                      },
                      {
                        label: 'Edit',
                        onClick: () => handleEditContent(item),
                        icon: 'edit'
                      },
                      {
                        label: 'Delete',
                        onClick: () => handleDeleteContent(item.id),
                        icon: 'trash',
                        variant: 'destructive'
                      }
                    ]}
                  />
                </div>

                <div className="content-body">
                  {item.subtitle && <p className="subtitle">{item.subtitle}</p>}
                  {item.description && <p className="description">{item.description}</p>}
                  {item.imageUrl && (
                    <div className="content-image">
                      <img src={item.imageUrl} alt={item.title} />
                    </div>
                  )}
                  <div className="content-meta">
                    <span className="section">{item.sectionName}</span>
                    <span className={`status ${item.isActive ? 'active' : 'inactive'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="pages-cms">
        <LoadingSpinner message="Loading CMS data..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pages-cms">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchInitialData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="pages-cms">
      <div className="cms-header">
        <h1>Pages CMS</h1>
        <p>Manage content for all pages and sections</p>
      </div>

      <div className="cms-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content Management
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'content' && renderContentTab()}
      </div>

      {/* Modals */}
      {isAddModalOpen && (
        <AddContentModal
          onClose={() => setIsAddModalOpen(false)}
          onSave={handleContentSaved}
          selectedPage={selectedPage}
          selectedSection={selectedSection}
          pages={pages}
          sections={sections}
        />
      )}

      {isEditModalOpen && editingContent && (
        <EditContentModal
          content={editingContent}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleContentSaved}
          pages={pages}
          sections={sections}
        />
      )}
    </div>
  );
};

export default PagesCMS;
